import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { estimateService, customerService, fileService } from '../services/apiService';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Plus, Trash2, Check, X, User, Phone, Calendar, Share2, Download, Search, ShoppingCart, Upload, Image as ImageIcon, CheckCircle, Loader2, ChevronLeft, ChevronRight, ImageOff, Camera, Images } from 'lucide-react';
import html2canvas from 'html2canvas';
import EstimateCard from '../components/EstimateCard';
import CombinedEstimateCard from '../components/CombinedEstimateCard';
import GoldRateGateDialog from '../components/GoldRateGateDialog';
import useGoldRateGate from '../hooks/useGoldRateGate';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PhoneInput, normalizePhone } from '../components/ui/phone-input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Estimates = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const estimateCardRef = useRef(null);
  const combinedCardRef = useRef(null);

  // State management
  const [view, setView] = useState('customers'); // 'customers', 'customer-detail', or 'all-estimates'
  const [customers, setCustomers] = useState([]);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');  // NEW: Search query
  // Read initial status filter from URL (?status=draft or ?status=confirmed)
  const [statusFilter, setStatusFilter] = useState(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('status');
    return s === 'draft' || s === 'confirmed' ? s : null;
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerEstimates, setCustomerEstimates] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // All estimates view
  const [allEstimates, setAllEstimates] = useState([]);
  const [allEstimatesSearch, setAllEstimatesSearch] = useState('');
  const [allEstimatesStatusFilter, setAllEstimatesStatusFilter] = useState(null);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [estimateToShare, setEstimateToShare] = useState(null);
  const [isCombinedShare, setIsCombinedShare] = useState(false);

  // Combine estimates feature
  const [selectedEstimates, setSelectedEstimates] = useState([]);
  const [showCombineMode, setShowCombineMode] = useState(false);

  // Exchange estimate (old gold)
  const [isExchangeDialogOpen, setIsExchangeDialogOpen] = useState(false);
  const [exchangeEstimates, setExchangeEstimates] = useState([]);
  const [exchangeForm, setExchangeForm] = useState({
    customerPhone: '', customerName: '',
    grossWeight: '', lessDust: '', lessStones: '',
    approxPurity: '', refiningPercentage: '',
    goldRate: '', cashRequired: false, cashDeductionPercentage: '',
  });
  const [exchangeStones, setExchangeStones] = useState([]);
  const [currentExchangeStone, setCurrentExchangeStone] = useState({ name: '', weight: '', rate: '', amount: '' });

  // Catalog data from API
  const [mainGroups, setMainGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [goldTypes, setGoldTypes] = useState([]);
  const [todayGoldPrices, setTodayGoldPrices] = useState(null); // Today's gold prices from cashier

  // Linked orders mapping: estimate_id -> [order_numbers]
  const [linkedOrders, setLinkedOrders] = useState({});

  // Form state
  const [estimateForm, setEstimateForm] = useState({
    customerPhone: '',
    customerName: '',
    tagNumber: '', // NEW: Tag/Reference number
    mainGroup: '',
    product: '',
    grossWeight: '',
    goldPurity: '22K', // Add gold purity field
    wastagePercentage: '8',
    goldRate: '',
    makingChargesType: 'per_gram', // 'per_gram' or 'fixed'
    makingChargesPerGram: '',
    fixedMakingCharge: '',
    otherCharges: '', // Additional charges
  });

  // Tag number stock lookup state
  const [tagStockMatch, setTagStockMatch] = useState(null);
  const [tagLookupLoading, setTagLookupLoading] = useState(false);
  const tagLookupTimer = useRef(null);

  const [stones, setStones] = useState([]);
  const [currentStone, setCurrentStone] = useState({
    name: '',
    weight: '',
    rate: '',
    amount: '', // Make amount editable
  });
  const [editingStoneIndex, setEditingStoneIndex] = useState(null); // Track which stone is being edited
  const [estimateStatus, setEstimateStatus] = useState('draft'); // Add status selection
  const [wizardStep, setWizardStep] = useState(1); // 4-step wizard

  // Image upload for estimates
  const [estimateImages, setEstimateImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const estimateImageInputRef = useRef(null);
  const estimateCameraInputRef = useRef(null);

  // Quick Add for stone weights
  const [accumulatedWeights, setAccumulatedWeights] = useState([]);
  const [showWeightTotal, setShowWeightTotal] = useState(false);
  // Refs to keep keyboard alive when navigating between stone fields
  const stoneNameInputRef   = useRef(null);
  const stoneWeightInputRef = useRef(null);
  const stoneRateInputRef   = useRef(null);
  const stoneAmountInputRef = useRef(null);


  // Calculated values
  const [calculatedValues, setCalculatedValues] = useState({
    stonesWeightCarats: 0,
    stonesWeightGrams: 0,
    netWeight: 0,
    goldWeightWithWastage: 0,
    goldAmount: 0,
    stonesAmount: 0,
    makingAmount: 0,
    subtotal: 0,
    gstAmount: 0,
    totalAmount: 0,
  });

  // Load customers on mount and when filter changes
  useEffect(() => {
    loadCustomers(statusFilter);
    loadCatalogData();
  }, [statusFilter]);

  // Auto-calculate when form changes (including stone edits)
  useEffect(() => {
    calculateEstimate();
  }, [estimateForm, stones, currentStone, editingStoneIndex]);

  const loadCatalogData = async () => {
    try {
      const [mainGroupsRes, productsRes, goldTypesRes, todayPriceRes] = await Promise.all([
        api.get('/catalog/main-groups'),
        api.get('/catalog/products'),
        api.get('/catalog/gold-types'),
        api.get('/gold-prices/latest'),
      ]);
      setMainGroups(mainGroupsRes.data.map(item => item.name));
      setProducts(productsRes.data.map(item => item.name));
      setGoldTypes(goldTypesRes.data.map(item => item.name));
      if (todayPriceRes.data.exists) {
        setTodayGoldPrices(todayPriceRes.data.prices);
      } else {
        setTodayGoldPrices(null);
      }
    } catch (error) {
      console.error('Error loading catalog:', error);
      setMainGroups([]);
      setProducts([]);
      setGoldTypes([]);
    }
  };

  const loadCustomers = async (status = null) => {
    try {
      setLoading(true);
      const data = await estimateService.getEstimatesGroupedByCustomer(status);
      setCustomers(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load customers',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllEstimates = async () => {
    try {
      setLoading(true);
      const data = await estimateService.getEstimates();
      setAllEstimates(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load estimates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerEstimates = async (customerPhone) => {
    try {
      setLoading(true);
      const [data, linkedRes] = await Promise.all([
        estimateService.getEstimates(customerPhone),
        api.get(`/estimates/linked-orders?phone=${customerPhone}`),
      ]);
      setCustomerEstimates(data);
      setLinkedOrders(linkedRes.data || {});
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load estimates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateEstimate = () => {
    const grossWeight = parseFloat(estimateForm.grossWeight) || 0;
    const wastagePercentage = parseFloat(estimateForm.wastagePercentage) || 0;
    const goldRate = parseFloat(estimateForm.goldRate) || 0;
    const otherCharges = parseFloat(estimateForm.otherCharges) || 0;

    // Build effective stones list (use currentStone values for the stone being edited)
    const effectiveStones = stones.map((stone, idx) => {
      if (editingStoneIndex === idx) {
        const w = parseFloat(currentStone.weight) || 0;
        const r = parseFloat(currentStone.rate) || 0;
        const amt = currentStone.amount ? parseFloat(currentStone.amount) : w * r;
        return { ...stone, weight: w, rate: r, amount: amt };
      }
      return stone;
    });

    // Step 1: Calculate stones weight in carats
    const stonesWeightCarats = effectiveStones.reduce((sum, stone) => sum + (parseFloat(stone.weight) || 0), 0);

    // Step 2: Convert to grams (1 carat = 0.2 grams)
    const stonesWeightGrams = stonesWeightCarats * 0.2;

    // Step 3: Calculate net weight
    const netWeight = grossWeight - stonesWeightGrams;

    // Step 4: Calculate gold weight with wastage
    const goldWeightWithWastage = netWeight * (1 + wastagePercentage / 100);

    // Step 5: Calculate gold amount (rounded to nearest rupee)
    const goldAmount = Math.round(goldWeightWithWastage * goldRate);

    // Step 6: Calculate stones amount — honour a manually-entered `amount`
    // (e.g. a flat ₹ for a lot). Falls back to weight × rate when not set.
    // Bug fix Feb-2026: previously ignored `amount` and always used w × r,
    // so the UI total drifted from the value the backend actually stored.
    const stonesAmount = Math.round(effectiveStones.reduce((sum, stone) => {
      const a = parseFloat(stone.amount);
      if (!isNaN(a)) return sum + a;
      const w = parseFloat(stone.weight) || 0;
      const r = parseFloat(stone.rate) || 0;
      return sum + (w * r);
    }, 0));

    // Step 7: Calculate making amount based on type (rounded)
    let makingAmount = 0;
    if (estimateForm.makingChargesType === 'per_gram') {
      const makingChargesPerGram = parseFloat(estimateForm.makingChargesPerGram) || 0;
      makingAmount = Math.round(netWeight * makingChargesPerGram);
    } else {
      // Fixed making charge (already a whole number usually)
      makingAmount = Math.round(parseFloat(estimateForm.fixedMakingCharge) || 0);
    }

    // Step 8: Calculate subtotal (including other charges, rounded)
    const roundedOtherCharges = Math.round(otherCharges);
    const subtotal = goldAmount + stonesAmount + makingAmount + roundedOtherCharges;

    // Step 9: Calculate GST (3%, rounded)
    const gstAmount = Math.round(subtotal * 0.03);

    // Step 10: Calculate total (rounded)
    const totalAmount = subtotal + gstAmount;

    setCalculatedValues({
      stonesWeightCarats,
      stonesWeightGrams,
      netWeight,
      goldWeightWithWastage,
      goldAmount,
      stonesAmount,
      makingAmount,
      subtotal,
      gstAmount,
      totalAmount,
    });
  };

  const handlePhoneChange = async (phone) => {
    // Functional update so we never clobber the latest phone the user is typing.
    setEstimateForm(prev => ({ ...prev, customerPhone: phone }));

    // Auto-fill customer name if exists
    if (phone.length >= 10) {
      try {
        const customers = await customerService.searchByPhone(phone);
        if (customers.length > 0) {
          // Do NOT write customerPhone back here (async result is stale relative
          // to what the user may have typed since) — only set the name.
          setEstimateForm(prev => ({
            ...prev,
            customerName: customers[0].name,
          }));
        }
      } catch (error) {
        console.error('Error searching customer:', error);
      }
    }
  };


  const evaluateFormula = (input) => {
    // Check if input starts with =
    if (typeof input === 'string' && input.trim().startsWith('=')) {
      try {
        // Remove = and evaluate safely (only allow numbers and basic operators)
        const formula = input.substring(1).trim();

        // Security: Only allow numbers, +, -, *, /, ., and parentheses
        if (!/^[0-9+\-*/().\s]+$/.test(formula)) {
          return input; // Return original if contains invalid characters
        }

        // Evaluate using Function constructor (safer than eval)
        const result = Function('"use strict"; return (' + formula + ')')();

        if (isNaN(result) || !isFinite(result)) {
          return input; // Return original if result is invalid
        }

        return result.toString();
      } catch (error) {
        return input; // Return original if evaluation fails
      }
    }
    return input;
  };

  const handleQuickAdd = () => {
    const weight = parseFloat(currentStone.weight);
    if (!weight || isNaN(weight)) {
      alert('Please enter a valid weight first');
      return;
    }

    setAccumulatedWeights([...accumulatedWeights, weight]);
    setShowWeightTotal(true);
    setCurrentStone({ ...currentStone, weight: '' }); // Clear for next entry
  };

  const handleUseTotal = () => {
    const total = accumulatedWeights.reduce((sum, w) => sum + w, 0);
    setCurrentStone({ ...currentStone, weight: total.toString() });
    setAccumulatedWeights([]);
    setShowWeightTotal(false);
  };

  const handleClearWeights = () => {
    setAccumulatedWeights([]);
    setShowWeightTotal(false);
  };

  const handleAddStone = () => {
    if (!currentStone.weight || !currentStone.rate) {
      toast({
        title: 'Error',
        description: 'Please enter stone weight and rate',
        variant: 'destructive',
      });
      return;
    }

    const weight = parseFloat(currentStone.weight);
    const rate = parseFloat(currentStone.rate);
    // Use manual amount if provided, otherwise calculate. Always store as
    // whole rupees so the UI, the saved doc and the eventual print/WhatsApp
    // all agree (Feb-2026 whole-rupee lock).
    const amount = Math.round(currentStone.amount ? parseFloat(currentStone.amount) : weight * rate);

    if (editingStoneIndex !== null) {
      // Update existing stone
      const updatedStones = [...stones];
      updatedStones[editingStoneIndex] = { ...currentStone, weight, rate, amount, id: stones[editingStoneIndex].id };
      setStones(updatedStones);
      setEditingStoneIndex(null);
    } else {
      // Add new stone
      setStones([...stones, { ...currentStone, weight, rate, amount, id: Date.now() }]);
    }

    setCurrentStone({ name: '', weight: '', rate: '', amount: '' });
    // Synchronous focus within user gesture — keeps keyboard up on iOS
    stoneNameInputRef.current?.focus();
  };

  const handleEditStone = (index) => {
    const stone = stones[index];
    setCurrentStone({
      name: stone.name,
      weight: stone.weight.toString(),
      rate: stone.rate.toString(),
      amount: '' // Leave empty so amount auto-calculates from weight × rate
    });
    setEditingStoneIndex(index);
  };

  const handleCancelEdit = () => {
    setCurrentStone({ name: '', weight: '', rate: '', amount: '' });
    setEditingStoneIndex(null);
  };

  const handleRemoveStone = (id) => {
    setStones(stones.filter(s => s.id !== id));
  };

  const formatEstimateMessage = (estimate) => {
    return `
*ESTIMATE DETAILS*
━━━━━━━━━━━━━━━━━━━

📋 *Estimate #:* ${estimate.estimate_number}
📅 *Date:* ${new Date(estimate.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}

💍 *Product Details*
${estimate.main_group ? `Main Group: ${estimate.main_group}` : ''}
${estimate.product ? `Product: ${estimate.product}` : ''}

⚖️ *Weight Details*
Gross Weight: ${estimate.gross_weight}g
Net Weight: ${estimate.net_weight}g
Wastage: ${estimate.wastage_percentage}%

🥇 *Gold*
Purity: ${estimate.gold_purity}
Rate: ₹${estimate.gold_rate.toLocaleString()}/g
Gold Amount: ₹${Math.round(estimate.gold_amount).toLocaleString()}

${estimate.stones && estimate.stones.length > 0 ? `
💎 *Stones (${estimate.stones.length})*
${estimate.stones.map((stone, idx) =>
  `${idx + 1}. ${stone.name}: ${stone.weight}ct @ ₹${stone.rate.toLocaleString()}/ct = ₹${Math.round(stone.amount).toLocaleString()}`
).join('\n')}
Stones Total: ₹${Math.round(estimate.stones_amount).toLocaleString()}
` : ''}
🔨 *Making Charges*
Type: ${estimate.making_charges_type === 'per_gram' ? 'Per Gram' : 'Fixed'}
${estimate.making_charges_type === 'per_gram'
  ? `Rate: ₹${estimate.making_charges_per_gram}/g`
  : `Fixed: ₹${estimate.fixed_making_charge}`}
Making Amount: ₹${Math.round(estimate.making_amount).toLocaleString()}

━━━━━━━━━━━━━━━━━━━
💰 *TOTAL (All Inclusive)*
*₹${Math.round(estimate.total_amount).toLocaleString()}*
━━━━━━━━━━━━━━━━━━━

Status: ${estimate.status === 'confirmed' ? '✅ Confirmed' : '📝 Draft'}
`.trim();
  };

  const handleShareEstimate = (estimate) => {
    setEstimateToShare(estimate);
    setIsCombinedShare(false);
    setShareDialogOpen(true);
  };

  const handleShareCombined = () => {
    setIsCombinedShare(true);
    setEstimateToShare(null);
    setShareDialogOpen(true);
  };

  const generateEstimateImage = async () => {
    const ref = isCombinedShare ? combinedCardRef.current : estimateCardRef.current;
    if (!ref) return null;

    try {
      const canvas = await html2canvas(ref, {
        scale: 2,
        backgroundColor: null,
        logging: false,
      });
      return canvas;
    } catch (error) {
      console.error('Error generating image:', error);
      return null;
    }
  };

  const getShareFilename = () => {
    if (isCombinedShare) return `combined-estimate-${selectedCustomer?.customer_name || 'combined'}.jpg`;
    return `estimate-${estimateToShare?.estimate_number || 'estimate'}.jpg`;
  };

  const canvasToBlob = (canvas) => {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
  };

  const shareNative = async () => {
    const canvas = await generateEstimateImage();
    if (!canvas) return;

    const blob = await canvasToBlob(canvas);
    const file = new File([blob], getShareFilename(), { type: 'image/jpeg' });

    // Use native Web Share API if available (mobile)
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'Estimate',
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      // Fallback: download the image
      const url = canvas.toDataURL('image/jpeg', 0.95);
      const link = document.createElement('a');
      link.download = getShareFilename();
      link.href = url;
      link.click();
      toast({ title: 'Image Downloaded!', description: 'Saved to your downloads' });
    }
    setShareDialogOpen(false);
  };

  const downloadImage = async () => {
    const canvas = await generateEstimateImage();
    if (!canvas) return;
    const url = canvas.toDataURL('image/jpeg', 0.95);
    const link = document.createElement('a');
    link.download = getShareFilename();
    link.href = url;
    link.click();
    toast({ title: 'Image Downloaded!', description: 'Saved to your downloads' });
    setShareDialogOpen(false);
  };

  const handleCreateEstimate = async () => {
    if (!estimateForm.customerPhone || !estimateForm.customerName) {
      toast({
        title: 'Error',
        description: 'Please enter customer phone and name',
        variant: 'destructive',
      });
      return;
    }

    if (!estimateForm.grossWeight || !estimateForm.goldRate) {
      toast({
        title: 'Error',
        description: 'Please enter gross weight and gold rate',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const estimateData = {
        customer_phone: estimateForm.customerPhone,
        customer_name: estimateForm.customerName,
        tag_number: estimateForm.tagNumber || null,  // Include tag number
        main_group: estimateForm.mainGroup,
        product: estimateForm.product,
        gross_weight: parseFloat(estimateForm.grossWeight),
        gold_purity: estimateForm.goldPurity,
        wastage_percentage: parseFloat(estimateForm.wastagePercentage),
        gold_rate: parseFloat(estimateForm.goldRate),
        stones: stones.map(s => ({
          name: s.name || 'Stone',
          weight: s.weight,
          rate: s.rate,
          amount: s.amount,
        })),
        making_charges_type: estimateForm.makingChargesType,
        making_charges_per_gram: parseFloat(estimateForm.makingChargesPerGram) || 0,
        fixed_making_charge: parseFloat(estimateForm.fixedMakingCharge) || 0,
        other_charges: parseFloat(estimateForm.otherCharges) || 0,
        status: estimateStatus,
        images: estimateImages.map(img => img.url),
      };

      await estimateService.createEstimate(estimateData);

      toast({
        title: 'Success',
        description: 'Estimate created successfully',
      });

      // Reset form
      setEstimateForm({
        customerPhone: selectedCustomer?.customer_phone || '',
        customerName: selectedCustomer?.customer_name || '',
        tagNumber: '',  // Reset tag number
        mainGroup: '',
        product: '',
        grossWeight: '',
        goldPurity: '22K',
        wastagePercentage: '8',
        goldRate: '',
        makingChargesType: 'per_gram',
        makingChargesPerGram: '',
        fixedMakingCharge: '',
        otherCharges: '',
      });
      setStones([]);
      setEstimateStatus('draft'); // Reset status
      setEstimateImages([]); // Reset images
      setIsCreateDialogOpen(false);

      // Reload data
      if (view === 'customer-detail') {
        loadCustomerEstimates(selectedCustomer.customer_phone);
      } else {
        loadCustomers(statusFilter);
      }
    } catch (error) {
      console.error('Error creating estimate:', error);
      // Handle error properly - extract message string from error object
      let errorMessage = 'Failed to create estimate';

      if (error.response?.data) {
        const errorData = error.response.data;
        // Check if it's a validation error with detail array
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (typeof errorData.detail === 'object' && errorData.detail !== null) {
          errorMessage = JSON.stringify(errorData.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEstimate = async (estimateNumber) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) {
      return;
    }

    try {
      await estimateService.deleteEstimate(estimateNumber);
      toast({
        title: 'Success',
        description: 'Estimate deleted successfully',
      });

      if (view === 'customer-detail') {
        loadCustomerEstimates(selectedCustomer.customer_phone);
      }
      loadCustomers(statusFilter);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete estimate',
        variant: 'destructive',
      });
    }
  };

  const toggleEstimateSelection = (estimate) => {
    if (selectedEstimates.find(e => e.estimate_number === estimate.estimate_number)) {
      setSelectedEstimates(selectedEstimates.filter(e => e.estimate_number !== estimate.estimate_number));
    } else {
      setSelectedEstimates([...selectedEstimates, estimate]);
    }
  };

  const calculateGrandTotal = () => {
    return selectedEstimates.reduce((sum, est) => sum + (est.total_amount || 0), 0);
  };

  const handleConfirmEstimate = async (estimateNumber) => {
    try {
      await estimateService.confirmEstimate(estimateNumber);
      toast({
        title: 'Success',
        description: 'Estimate confirmed successfully',
      });

      if (view === 'customer-detail') {
        loadCustomerEstimates(selectedCustomer.customer_phone);
      }
      loadCustomers(statusFilter);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to confirm estimate',
        variant: 'destructive',
      });
    }
  };

  const handleDuplicateEstimate = (estimate) => {
    // Just pre-fill form with estimate data for editing
    // Don't call backend API - let user edit and save manually
    setEstimateForm({
      customerPhone: estimate.customer_phone,
      customerName: estimate.customer_name,
      tagNumber: estimate.tag_number || '',
      mainGroup: estimate.main_group || '',
      product: estimate.product || '',
      grossWeight: estimate.gross_weight.toString(),
      goldPurity: estimate.gold_purity || '22K',
      wastagePercentage: estimate.wastage_percentage.toString(),
      goldRate: estimate.gold_rate.toString(),
      makingChargesType: estimate.making_charges_type || 'per_gram',
      makingChargesPerGram: estimate.making_charges_per_gram?.toString() || '',
      fixedMakingCharge: estimate.fixed_making_charge?.toString() || '',
      otherCharges: estimate.other_charges?.toString() || '',
    });

    setStones(estimate.stones.map((s, idx) => ({
      ...s,
      id: Date.now() + idx,
    })));

    setEstimateStatus(estimate.status); // Copy status too

    // Copy images if any
    setEstimateImages((estimate.images || []).map((url, i) => ({ url, name: `Photo ${i + 1}` })));

    setWizardStep(1);
    setIsCreateDialogOpen(true);

    toast({
      title: 'Ready to edit',
      description: 'Edit the estimate and click Save to create a new copy',
    });
  };

  const handleCreateOrderFromEstimate = (estimate) => {
    // Build stone details text from the estimate's stones
    const stoneDetailsText = estimate.stones && estimate.stones.length > 0
      ? estimate.stones.map(s => `${s.name || 'Stone'}: ${s.weight}ct @ ₹${s.rate}/ct`).join(', ')
      : '';

    // Navigate to messenger with estimate data to pre-fill the order form
    navigate('/messenger', {
      state: {
        fromEstimate: true,
        estimateData: {
          customerPhone: estimate.customer_phone,
          customerName: estimate.customer_name,
          mainGroup: estimate.main_group || '',
          title: estimate.product || '',
          productWeight: estimate.gross_weight?.toString() || '',
          karatPurity: estimate.gold_purity || '',
          goldRatePerGram: estimate.gold_rate?.toString() || '',
          goldRateType: 'fixed',
          stoneDetails: stoneDetailsText,
          priceAmount: estimate.total_amount?.toString() || '',
          linkedEstimateId: estimate.id,
          estimateNumber: estimate.estimate_number,
        },
      },
    });
  };

  const openCustomerDetail = (customer) => {
    setSelectedCustomer(customer);
    loadCustomerEstimates(customer.customer_phone);
    loadExchangeEstimates(customer.customer_phone);
    setView('customer-detail');
  };

  // Exchange estimate functions
  const loadExchangeEstimates = async (phone) => {
    try {
      const res = await api.get(`/exchange-estimates?phone=${phone}`);
      setExchangeEstimates(res.data);
    } catch (error) {
      setExchangeEstimates([]);
    }
  };

  const openExchangeDialog = (customerData = null) => {
    if (!todayGoldPrices) {
      toast({ title: "Gold Price Not Set", description: "Today's gold price has not been entered yet. Please contact the cashier.", variant: 'destructive' });
      return;
    }
    const defaultPurity = '22K';
    const rate = todayGoldPrices[defaultPurity] || '';
    setExchangeForm({
      customerPhone: customerData?.customer_phone || '',
      customerName: customerData?.customer_name || '',
      grossWeight: '', lessDust: '', lessStones: '',
      approxPurity: '', refiningPercentage: '',
      goldRate: rate ? String(rate) : '', cashRequired: false, cashDeductionPercentage: '',
    });
    setExchangeStones([]);
    setCurrentExchangeStone({ name: '', weight: '', rate: '', amount: '' });
    setIsExchangeDialogOpen(true);
  };

  const exchangeCalc = (() => {
    const gw = parseFloat(exchangeForm.grossWeight) || 0;
    const ld = parseFloat(exchangeForm.lessDust) || 0;
    const ls = parseFloat(exchangeForm.lessStones) || 0;
    const netWeight = gw - ld - ls;
    const purity = parseFloat(exchangeForm.approxPurity) || 0;
    const fineWeight = netWeight * (purity / 100);
    const refPct = parseFloat(exchangeForm.refiningPercentage) || 0;
    const finalGoldWeight = fineWeight * (1 - refPct / 100);
    const goldRate = parseFloat(exchangeForm.goldRate) || 0;
    const goldValue = Math.round(finalGoldWeight * goldRate);
    const stonesTotal = Math.round(exchangeStones.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0));
    const exchangeValue = goldValue + stonesTotal;
    const cashPct = parseFloat(exchangeForm.cashDeductionPercentage) || 0;
    const finalCash = exchangeForm.cashRequired ? Math.round(exchangeValue * (1 - cashPct / 100)) : 0;
    return { netWeight, fineWeight, finalGoldWeight, goldValue, stonesTotal, exchangeValue, finalCash };
  })();

  const handleAddExchangeStone = () => {
    const w = parseFloat(currentExchangeStone.weight);
    const r = parseFloat(currentExchangeStone.rate);
    if (!w || !r) { toast({ title: 'Enter weight and rate', variant: 'destructive' }); return; }
    const amount = currentExchangeStone.amount ? parseFloat(currentExchangeStone.amount) : w * r;
    setExchangeStones(prev => [...prev, { name: currentExchangeStone.name, weight: w, rate: r, amount }]);
    setCurrentExchangeStone({ name: '', weight: '', rate: '', amount: '' });
  };

  const handleCreateExchangeEstimate = async () => {
    if (!exchangeForm.customerPhone || !exchangeForm.customerName || !exchangeForm.grossWeight || !exchangeForm.approxPurity || !exchangeForm.goldRate) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }
    try {
      await api.post('/exchange-estimates', {
        customer_phone: exchangeForm.customerPhone,
        customer_name: exchangeForm.customerName,
        gross_weight: parseFloat(exchangeForm.grossWeight),
        less_dust: parseFloat(exchangeForm.lessDust) || 0,
        less_stones: parseFloat(exchangeForm.lessStones) || 0,
        approx_purity: parseFloat(exchangeForm.approxPurity),
        refining_percentage: parseFloat(exchangeForm.refiningPercentage) || 0,
        gold_rate: parseFloat(exchangeForm.goldRate),
        cash_required: exchangeForm.cashRequired,
        cash_deduction_percentage: parseFloat(exchangeForm.cashDeductionPercentage) || 0,
        stones: exchangeStones,
        stones_total: exchangeCalc.stonesTotal,
      });
      toast({ title: 'Success', description: 'Exchange estimate created!' });
      setIsExchangeDialogOpen(false);
      if (selectedCustomer) {
        loadExchangeEstimates(selectedCustomer.customer_phone);
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create exchange estimate', variant: 'destructive' });
    }
  };

  const handleDeleteExchange = async (estimateNumber) => {
    try {
      await api.delete(`/exchange-estimates/${estimateNumber}`);
      toast({ title: 'Deleted', description: 'Exchange estimate deleted' });
      if (selectedCustomer) loadExchangeEstimates(selectedCustomer.customer_phone);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  // Filter customers based on search query
  const filteredCustomers = customers.filter((customer) => {
    if (!customerSearchQuery) return true;

    const query = customerSearchQuery.toLowerCase();

    // Search by customer name or phone
    return (
      customer.customer_name?.toLowerCase().includes(query) ||
      customer.customer_phone?.includes(query)
    );
  });

  // Tag number stock lookup — debounced
  useEffect(() => {
    const tag = estimateForm.tagNumber?.trim();
    if (!tag) { setTagStockMatch(null); return; }

    clearTimeout(tagLookupTimer.current);
    setTagLookupLoading(true);
    tagLookupTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/stock-estimates/by-tag/${encodeURIComponent(tag)}`);
        setTagStockMatch(res.data);
      } catch {
        setTagStockMatch(null);
      } finally {
        setTagLookupLoading(false);
      }
    }, 500);
    return () => clearTimeout(tagLookupTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateForm.tagNumber]);

  const applyEstimateStockPrefill = () => {
    if (!tagStockMatch) return;
    const s = tagStockMatch;
    setEstimateForm(f => ({
      ...f,
      mainGroup: s.main_group || f.mainGroup,
      product: s.product || f.product,
      grossWeight: s.gross_weight != null ? String(s.gross_weight) : f.grossWeight,
      goldPurity: s.gold_purity || f.goldPurity,
      wastagePercentage: s.wastage_percentage != null ? String(s.wastage_percentage) : f.wastagePercentage,
      goldRate: s.gold_rate ? String(s.gold_rate) : f.goldRate,
      makingChargesType: s.making_charges_type || f.makingChargesType,
      makingChargesPerGram: s.making_charges_per_gram != null ? String(s.making_charges_per_gram) : f.makingChargesPerGram,
      fixedMakingCharge: s.fixed_making_charge != null ? String(s.fixed_making_charge) : f.fixedMakingCharge,
      otherCharges: s.other_charges != null ? String(s.other_charges) : f.otherCharges,
    }));
    if (s.stones?.length) setStones(s.stones);
    setTagStockMatch(null);
  };

  const goldGate = useGoldRateGate();
  const openCreateDialog = async (customerData = null) => {
    setTagStockMatch(null);
    setTagLookupLoading(false);
    // Always gate first — /gold-prices/latest (which populates todayGoldPrices)
    // returns the newest doc across ALL dates, so relying on it here would let
    // a stale prior-day doc slip past the gate. Delegate to the shared hook.
    const ok = await goldGate.check();
    if (!ok) return;
    if (customerData) {
      setEstimateForm({
        ...estimateForm,
        customerPhone: customerData.customer_phone,
        customerName: customerData.customer_name,
        goldRate: todayGoldPrices?.[estimateForm.goldPurity] ? String(todayGoldPrices[estimateForm.goldPurity]) : '',
      });
    } else {
      setEstimateForm({
        ...estimateForm,
        goldRate: todayGoldPrices?.[estimateForm.goldPurity] ? String(todayGoldPrices[estimateForm.goldPurity]) : '',
      });
    }
    setWizardStep(1);
    setIsCreateDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return `₹${Math.round(parseFloat(amount)).toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  };

  // Render customers list view
  const renderCustomersView = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Estimates by Customer</h2>

        {/* Status Filter Toggle */}
        <div className="flex gap-2 mb-4" data-testid="status-filter">
          <button
            onClick={() => setStatusFilter(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              statusFilter === null
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="filter-all"
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter('confirmed')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              statusFilter === 'confirmed'
                ? 'bg-green-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="filter-confirmed"
          >
            Confirmed
          </button>
          <button
            onClick={() => setStatusFilter('draft')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              statusFilter === 'draft'
                ? 'bg-yellow-500 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="filter-draft"
          >
            Draft
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by customer name, phone, or tag number..."
              value={customerSearchQuery}
              onChange={(e) => setCustomerSearchQuery(e.target.value)}
              className="pl-10 py-6 text-base"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading customers...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-12">
            {customerSearchQuery ? (
              <p className="text-gray-500">No customers found matching "{customerSearchQuery}"</p>
            ) : (
              <>
                <p className="text-gray-500 mb-4">No estimates created yet</p>
                <Button onClick={() => openCreateDialog()} className="bg-emerald-500 hover:bg-emerald-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Estimate
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCustomers.map((customer) => (
              <div
                key={customer.customer_phone}
                onClick={() => openCustomerDetail(customer)}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{customer.customer_name}</h3>
                      <p className="text-sm text-gray-500 flex items-center">
                        <Phone className="w-3 h-3 mr-1" />
                        {customer.customer_phone}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-emerald-600">{customer.estimate_count}</p>
                    <p className="text-xs text-gray-500">estimates</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Filtered all-estimates list
  const filteredAllEstimates = allEstimates.filter((est) => {
    // Status filter
    if (allEstimatesStatusFilter === 'confirmed' && est.status !== 'confirmed') return false;
    if (allEstimatesStatusFilter === 'draft' && est.status !== 'draft') return false;
    // Search filter
    if (!allEstimatesSearch) return true;
    const q = allEstimatesSearch.toLowerCase();
    return (
      est.customer_name?.toLowerCase().includes(q) ||
      est.customer_phone?.includes(q) ||
      est.estimate_number?.toLowerCase().includes(q) ||
      est.tag_number?.toLowerCase().includes(q) ||
      est.product?.toLowerCase().includes(q) ||
      est.main_group?.toLowerCase().includes(q)
    );
  });

  // Render all estimates flat list view
  const renderAllEstimatesView = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">All Estimates</h2>

        {/* Status Filter */}
        <div className="flex gap-2 mb-4" data-testid="all-est-status-filter">
          <button
            onClick={() => setAllEstimatesStatusFilter(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              allEstimatesStatusFilter === null ? 'bg-emerald-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >All</button>
          <button
            onClick={() => setAllEstimatesStatusFilter('confirmed')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              allEstimatesStatusFilter === 'confirmed' ? 'bg-green-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >Confirmed</button>
          <button
            onClick={() => setAllEstimatesStatusFilter('draft')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              allEstimatesStatusFilter === 'draft' ? 'bg-yellow-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >Draft</button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name, phone, estimate #, tag, product..."
              value={allEstimatesSearch}
              onChange={(e) => setAllEstimatesSearch(e.target.value)}
              className="pl-10 py-6 text-base"
              data-testid="all-est-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading estimates...</p>
          </div>
        ) : filteredAllEstimates.length === 0 ? (
          <div className="text-center py-12">
            {allEstimatesSearch ? (
              <p className="text-gray-500">No estimates matching "{allEstimatesSearch}"</p>
            ) : (
              <p className="text-gray-500">No estimates found</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">{filteredAllEstimates.length} estimate{filteredAllEstimates.length !== 1 ? 's' : ''}</p>
            {filteredAllEstimates.map((estimate) => (
              <div
                key={estimate.estimate_number}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
                onClick={() => {
                  // Navigate to customer detail for this estimate's customer
                  openCustomerDetail({ customer_phone: estimate.customer_phone, customer_name: estimate.customer_name });
                }}
                data-testid={`all-est-card-${estimate.estimate_number}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1 mr-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-800">{estimate.estimate_number}</h3>
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                        estimate.status === 'confirmed'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {estimate.status === 'confirmed' ? 'Confirmed' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 flex items-center">
                      <User className="w-3 h-3 mr-1 flex-shrink-0" />
                      {estimate.customer_name}
                      <span className="mx-1 text-gray-300">|</span>
                      <Phone className="w-3 h-3 mr-1 flex-shrink-0" />
                      {estimate.customer_phone}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {formatDate(estimate.created_at)}
                    </p>
                    {estimate.tag_number && (
                      <p className="text-xs text-blue-600 font-semibold mt-1">Tag: {estimate.tag_number}</p>
                    )}
                    {(estimate.main_group || estimate.product) && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {estimate.main_group && estimate.product
                          ? `${estimate.main_group} - ${estimate.product}`
                          : estimate.main_group || estimate.product}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(estimate.total_amount)}</p>
                    <p className="text-xs text-gray-400 mt-1">{estimate.gross_weight}g gross</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render customer detail view
  const renderCustomerDetailView = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-4">
          <Button
            variant="ghost"
            onClick={() => {
              setView('customers');
              setSelectedCustomer(null);
              loadCustomers(statusFilter);
            }}
            className="mr-3"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{selectedCustomer?.customer_name}</h2>
            <p className="text-sm text-gray-500">{selectedCustomer?.customer_phone}</p>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => currentUser?.role === 'admin' ? openCreateDialog(selectedCustomer) : null}
            disabled={currentUser?.role !== 'admin'}
            className={`flex-1 text-sm h-9 px-3 ${currentUser?.role === 'admin' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'}`}
            title={currentUser?.role !== 'admin' ? 'Create estimates via Sessions & Leads' : ''}
            data-testid="detail-add-estimate-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Estimate
          </Button>
          <Button
            onClick={() => currentUser?.role === 'admin' ? openExchangeDialog(selectedCustomer) : null}
            disabled={currentUser?.role !== 'admin'}
            className={`text-sm h-9 px-3 ${currentUser?.role === 'admin' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-amber-200 text-amber-400 cursor-not-allowed opacity-60'}`}
            title={currentUser?.role !== 'admin' ? 'Create Old Gold estimates via Sessions & Leads' : ''}
            data-testid="detail-old-gold-btn"
          >
            Old Gold
          </Button>
        </div>


        {/* Combine Mode Toggle & Grand Total */}
        {customerEstimates.length > 1 && (
          <div className="mb-4 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => {
                setShowCombineMode(!showCombineMode);
                if (!showCombineMode) setSelectedEstimates([]);
              }}
              className={showCombineMode ? 'bg-blue-50 border-blue-500' : ''}
            >
              {showCombineMode ? 'Cancel Selection' : '🔗 Combine Estimates'}
            </Button>

            {selectedEstimates.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="bg-emerald-50 border-2 border-emerald-500 rounded-lg px-4 py-2">
                  <p className="text-sm text-gray-600">
                    {selectedEstimates.length} selected
                  </p>
                  <p className="text-xl font-bold text-emerald-700">
                    Grand Total: {formatCurrency(calculateGrandTotal())}
                  </p>
                </div>
                <Button
                  onClick={handleShareCombined}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="share-combined-btn"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Share
                </Button>
              </div>
            )}
          </div>
        )}


        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : customerEstimates.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-500">No estimates for this customer</p>
          </div>
        ) : (
          <div className="space-y-3">
            {customerEstimates.map((estimate) => (
              <div
                key={estimate.estimate_number}
                className={`bg-white border rounded-lg p-4 ${
                  showCombineMode && selectedEstimates.find(e => e.estimate_number === estimate.estimate_number)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200'
                }`}
                onClick={() => {
                  if (showCombineMode) {
                    toggleEstimateSelection(estimate);
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (!showCombineMode) handleDuplicateEstimate(estimate);
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3">
                    {/* Checkbox for combine mode */}
                    {showCombineMode && (
                      <input
                        type="checkbox"
                        checked={selectedEstimates.find(e => e.estimate_number === estimate.estimate_number) !== undefined}
                        onChange={() => toggleEstimateSelection(estimate)}
                        className="w-5 h-5 text-blue-600 mt-1"
                      />
                    )}
                    <div>
                      <h3 className="font-bold text-lg text-gray-800">{estimate.estimate_number}</h3>
                      <p className="text-sm text-gray-500 flex items-center mt-1">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(estimate.created_at)}
                      </p>
                      {estimate.tag_number && (
                        <p className="text-xs text-blue-600 font-semibold mt-1">
                          🏷️ Tag: {estimate.tag_number}
                        </p>
                      )}
                      {linkedOrders[estimate.id] && linkedOrders[estimate.id].length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`linked-order-badge-${estimate.estimate_number}`}>
                          {linkedOrders[estimate.id].map((orderNum) => (
                            <span
                              key={orderNum}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-300"
                            >
                              <ShoppingCart className="w-3 h-3 mr-1" />
                              {orderNum}
                            </span>
                          ))}
                        </div>
                      )}
                      {(estimate.main_group || estimate.product) && (
                        <p className="text-xs text-gray-600 mt-1">
                          {estimate.main_group && estimate.product
                            ? `${estimate.main_group} - ${estimate.product}`
                            : estimate.main_group || estimate.product
                          }
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-emerald-600">
                      {formatCurrency(estimate.total_amount)}
                    </p>
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mt-1 ${
                      estimate.status === 'confirmed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {estimate.status === 'confirmed' ? 'Confirmed' : 'Draft'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <span className="text-gray-500">Gross Weight:</span>
                    <span className="ml-2 font-medium">{estimate.gross_weight}g</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Net Weight:</span>
                    <span className="ml-2 font-medium">{(parseFloat(estimate.net_weight) || 0).toFixed(2)}g</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Gold Rate:</span>
                    <span className="ml-2 font-medium">₹{estimate.gold_rate}/g</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Wastage:</span>
                    <span className="ml-2 font-medium">{estimate.wastage_percentage}%</span>
                  </div>
                </div>

                {estimate.stones.length > 0 && (
                  <div className="mb-3 p-2 bg-purple-50 rounded">
                    <p className="text-xs font-medium text-purple-700 mb-1">Stones ({estimate.stones.length})</p>
                    <div className="space-y-1">
                      {estimate.stones.map((stone, idx) => (
                        <div key={idx} className="text-xs text-gray-600">
                          {stone.name || 'Stone'}: {stone.weight}ct @ ₹{stone.rate}/ct = {formatCurrency(stone.amount)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Estimate Images */}
                {estimate.images && estimate.images.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {estimate.images.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:opacity-80 transition-opacity">
                        <img src={url} alt={`Estimate photo ${idx + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}

                {!showCombineMode && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleCreateOrderFromEstimate(estimate)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid={`create-order-btn-${estimate.estimate_number}`}
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      Create Order
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleShareEstimate(estimate)}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Share
                    </Button>
                    {estimate.status === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => handleConfirmEstimate(estimate.estimate_number)}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Confirm
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDuplicateEstimate(estimate)}
                      className="border-blue-500 text-blue-500 hover:bg-blue-50"
                    >
                      Duplicate
                    </Button>
                    {currentUser?.role === 'admin' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteEstimate(estimate.estimate_number)}
                        className="border-red-500 text-red-500 hover:bg-red-50"
                        data-testid={`delete-estimate-btn-${estimate.estimate_number}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Exchange Estimates Section */}
        {exchangeEstimates.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-bold text-amber-700 mb-3">Old Gold Exchange Estimates</h3>
            <div className="space-y-3">
              {exchangeEstimates.map((ex) => (
                <div key={ex.id} className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-bold text-amber-800">{ex.estimate_number}</p>
                      <p className="text-xs text-gray-500">{new Date(ex.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-amber-700">{formatCurrency(ex.exchange_value)}</p>
                      {ex.cash_required && <p className="text-sm text-green-600 font-semibold">Cash: {formatCurrency(ex.final_cash_amount)}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600">
                    <p>Gross: {ex.gross_weight}g</p>
                    <p>Purity: {ex.approx_purity}%</p>
                    <p>Refining: {ex.refining_percentage}%</p>
                    <p>Rate: {formatCurrency(ex.gold_rate)}/g</p>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Button size="sm" variant="outline" onClick={() => handleDeleteExchange(ex.estimate_number)} className="border-red-400 text-red-500 hover:bg-red-50">
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render create estimate dialog (4-step wizard)
  const renderCreateDialog = () => {
    const handleWizardNext = () => {
      if (wizardStep === 1) {
        if (!estimateForm.customerPhone || !estimateForm.customerName) {
          toast({ title: 'Required', description: 'Please enter customer phone and name', variant: 'destructive' });
          return;
        }
      }
      if (wizardStep === 2) {
        if (!estimateForm.grossWeight || !estimateForm.goldRate) {
          toast({ title: 'Required', description: 'Please enter gross weight and gold rate', variant: 'destructive' });
          return;
        }
      }
      setWizardStep(s => s + 1);
    };

    const stepTitles = ['Customer & Item', 'Gold Details', 'Stones', 'Charges & Review'];

    const renderStep1 = () => (
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-base">Customer Phone *</Label>
            <PhoneInput
              value={normalizePhone(estimateForm.customerPhone)}
              onChange={val => handlePhoneChange(val)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customerName" className="text-base">Customer Name *</Label>
            <Input
              id="customerName"
              value={estimateForm.customerName}
              onChange={(e) => setEstimateForm({ ...estimateForm, customerName: e.target.value })}
              className="text-base py-6"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tagNumber" className="text-base">Tag Number</Label>
          <div className="relative">
            <Input
              id="tagNumber"
              type="text"
              inputMode="numeric"
              value={estimateForm.tagNumber}
              onChange={(e) => setEstimateForm({ ...estimateForm, tagNumber: e.target.value })}
              placeholder="Tag/Reference number (optional)"
              className="text-base py-6"
            />
            {tagLookupLoading && (
              <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
            )}
          </div>
          {tagStockMatch && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 gap-2">
              <div className="text-sm text-amber-800 min-w-0">
                <span className="font-semibold">Stock match found:</span>{' '}
                <span className="text-xs">{tagStockMatch.product} · {tagStockMatch.gold_purity} · {tagStockMatch.gross_weight}g</span>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={applyEstimateStockPrefill}
                className="flex-shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-xs h-7 px-3"
                data-testid="use-stock-data-btn"
              >
                <CheckCircle className="w-3 h-3 mr-1" /> Use Stock Data
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-base">Main Group</Label>
            <Select
              value={estimateForm.mainGroup}
              onValueChange={(value) => setEstimateForm({ ...estimateForm, mainGroup: value })}
            >
              <SelectTrigger className="text-base py-6">
                <SelectValue placeholder="Select group" />
              </SelectTrigger>
              <SelectContent>
                {mainGroups.map((group) => (
                  <SelectItem key={group} value={group}>{group}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-base">Product</Label>
            <Select
              value={estimateForm.product}
              onValueChange={(value) => setEstimateForm({ ...estimateForm, product: value })}
            >
              <SelectTrigger className="text-base py-6">
                <SelectValue placeholder="Select product" />
              </SelectTrigger>
              <SelectContent>
                {products.map((prod) => (
                  <SelectItem key={prod} value={prod}>{prod}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );

    const renderStep2 = () => (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="grossWeight" className="text-base">Gross Weight (grams) *</Label>
            <Input
              id="grossWeight"
              type="number"
              step="0.01"
              inputMode="decimal"
              value={estimateForm.grossWeight}
              onChange={(e) => setEstimateForm({ ...estimateForm, grossWeight: e.target.value })}
              className="text-base py-6"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-base">Gold Purity *</Label>
            <div className="flex flex-wrap gap-2">
              {(goldTypes.length > 0 ? goldTypes : ['9K', '14K', '18K', '22K']).map((purity) => (
                <button
                  key={purity}
                  type="button"
                  onClick={() => {
                    const newRate = todayGoldPrices?.[purity];
                    setEstimateForm({
                      ...estimateForm,
                      goldPurity: purity,
                      goldRate: newRate ? String(newRate) : estimateForm.goldRate,
                    });
                  }}
                  className={`py-3 px-4 text-base font-semibold rounded-lg border-2 transition-all ${
                    estimateForm.goldPurity === purity
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400'
                  }`}
                >
                  {purity}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-base">Net Weight (grams)</Label>
            <Input
              value={calculatedValues.netWeight.toFixed(2)}
              readOnly
              className="bg-emerald-50 text-emerald-700 font-semibold text-base py-6"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wastagePercentage" className="text-base">Wastage %</Label>
            <Input
              id="wastagePercentage"
              type="number"
              step="0.1"
              inputMode="decimal"
              value={estimateForm.wastagePercentage}
              onChange={(e) => setEstimateForm({ ...estimateForm, wastagePercentage: e.target.value })}
              className="text-base py-6"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="goldRate" className="text-base">Gold Rate (₹/gram) *</Label>
            <Input
              id="goldRate"
              type="number"
              inputMode="numeric"
              value={estimateForm.goldRate}
              onChange={(e) => setEstimateForm({ ...estimateForm, goldRate: e.target.value })}
              className="text-base py-6"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-base">Gold Amount</Label>
            <Input
              value={formatCurrency(calculatedValues.goldAmount)}
              readOnly
              className="bg-emerald-50 text-emerald-700 font-bold text-base py-6"
            />
          </div>
        </div>
      </div>
    );

    const renderStep3 = () => (
      <div className="space-y-3">
        <p className="text-sm text-gray-500">Add stones below. Tap Next to skip if no stones.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="stoneName">Stone Name</Label>
            <Input
              id="stoneName"
              ref={stoneNameInputRef}
              enterKeyHint="next"
              value={currentStone.name}
              onChange={(e) => setCurrentStone({ ...currentStone, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); stoneWeightInputRef.current?.focus(); } }}
              className="text-base py-6"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stoneWeight">Weight (carats)</Label>
            <div className="flex space-x-2">
              <Input
                id="stoneWeight"
                ref={stoneWeightInputRef}
                type="number"
                step="0.01"
                inputMode="decimal"
                enterKeyHint="next"
                value={currentStone.weight}
                onChange={(e) => {
                  const w = e.target.value;
                  const autoAmt = w && currentStone.rate ? String(parseFloat(w) * parseFloat(currentStone.rate)) : '';
                  setCurrentStone({ ...currentStone, weight: w, amount: autoAmt });
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); stoneRateInputRef.current?.focus(); } }}
                className="text-base py-6 flex-1"
              />
              <Button
                type="button"
                onPointerDown={(e) => e.preventDefault()}
                onClick={handleQuickAdd}
                className="bg-purple-500 hover:bg-purple-600 text-white whitespace-nowrap"
              >
                + Add
              </Button>
            </div>
            {showWeightTotal && accumulatedWeights.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded p-2">
                <div className="flex items-center justify-between text-sm">
                  <p className="text-purple-700 font-medium">
                    {accumulatedWeights.join(' + ')} = {accumulatedWeights.reduce((sum, w) => sum + w, 0).toFixed(2)}ct
                  </p>
                  <div className="flex space-x-1">
                    <Button type="button" size="sm" onPointerDown={(e) => e.preventDefault()} onClick={handleUseTotal} className="bg-green-500 hover:bg-green-600 text-white text-xs">
                      Use Total
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onPointerDown={(e) => e.preventDefault()} onClick={handleClearWeights} className="text-red-500 text-xs">
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="stoneRate">Rate (₹/carat)</Label>
            <Input
              id="stoneRate"
              ref={stoneRateInputRef}
              type="number"
              inputMode="numeric"
              enterKeyHint="next"
              value={currentStone.rate}
              onChange={(e) => {
                const r = e.target.value;
                const autoAmt = r && currentStone.weight ? String(parseFloat(currentStone.weight) * parseFloat(r)) : '';
                setCurrentStone({ ...currentStone, rate: r, amount: autoAmt });
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); stoneAmountInputRef.current?.focus(); } }}
              className="text-base py-6"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stoneAmount">Amount (₹) - Override if needed</Label>
            <Input
              id="stoneAmount"
              ref={stoneAmountInputRef}
              type="number"
              inputMode="numeric"
              enterKeyHint="done"
              value={currentStone.amount}
              onChange={(e) => setCurrentStone({ ...currentStone, amount: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddStone(); } }}
              className="text-base font-medium text-purple-600 py-6"
            />
          </div>
        </div>

        <Button
          type="button"
          onPointerDown={(e) => e.preventDefault()}
          onClick={handleAddStone}
          className="w-full bg-purple-500 hover:bg-purple-600 text-base py-6"
          data-testid="add-stone-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          {editingStoneIndex !== null ? 'Update Stone' : 'Add Stone'}
        </Button>

        {editingStoneIndex !== null && (
          <Button
            type="button"
            variant="outline"
            onPointerDown={(e) => e.preventDefault()}
            onClick={handleCancelEdit}
            className="w-full text-base py-6"
          >
            Cancel Edit
          </Button>
        )}

        {stones.length > 0 && (
          <div className="space-y-2 mt-2">
            <p className="text-sm font-semibold text-purple-700">Added Stones ({stones.length}):</p>
            {stones.map((stone, index) => (
              <div
                key={stone.id}
                onClick={() => handleEditStone(index)}
                className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  editingStoneIndex === index
                    ? 'bg-purple-100 border-purple-500 shadow-md'
                    : 'bg-purple-50 border-purple-200 hover:border-purple-400 hover:bg-purple-100'
                }`}
              >
                <span className="text-base flex-1">
                  <strong>{stone.name || 'Stone'}</strong>: {stone.weight}ct @ ₹{stone.rate.toLocaleString()}/ct = <strong className="text-purple-600">{formatCurrency(parseFloat(stone.weight) * parseFloat(stone.rate) || stone.amount)}</strong>
                  {editingStoneIndex === index && (
                    <span className="block text-xs text-purple-600 mt-1">Editing — update fields above</span>
                  )}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); handleRemoveStone(stone.id); }}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            ))}
            <div className="text-right text-base font-bold text-purple-700 bg-purple-100 px-4 py-2 rounded">
              Total Stones: {formatCurrency(calculatedValues.stonesAmount)}
            </div>
          </div>
        )}
      </div>
    );

    const renderStep4 = () => (
      <div className="space-y-5">
        <div className="space-y-4">
          <div>
            <Label className="text-base font-semibold">Making Charges Type</Label>
            <div className="flex items-center space-x-6 mt-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={estimateForm.makingChargesType === 'per_gram'}
                  onChange={() => setEstimateForm({ ...estimateForm, makingChargesType: 'per_gram' })}
                  className="w-4 h-4 text-emerald-600"
                />
                <span className="text-sm">Per Gram</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={estimateForm.makingChargesType === 'fixed'}
                  onChange={() => setEstimateForm({ ...estimateForm, makingChargesType: 'fixed' })}
                  className="w-4 h-4 text-emerald-600"
                />
                <span className="text-sm">Fixed Amount</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {estimateForm.makingChargesType === 'per_gram' ? (
              <div className="space-y-2">
                <Label htmlFor="makingCharges" className="text-base">Making Charges (₹/gram)</Label>
                <Input
                  id="makingCharges"
                  type="number"
                  inputMode="numeric"
                  value={estimateForm.makingChargesPerGram}
                  onChange={(e) => setEstimateForm({ ...estimateForm, makingChargesPerGram: e.target.value })}
                  className="text-base py-6"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="fixedMakingCharge" className="text-base">Fixed Making Charge (₹)</Label>
                <Input
                  id="fixedMakingCharge"
                  type="number"
                  inputMode="numeric"
                  value={estimateForm.fixedMakingCharge}
                  onChange={(e) => setEstimateForm({ ...estimateForm, fixedMakingCharge: e.target.value })}
                  className="text-base py-6"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-base">Making Amount</Label>
              <Input
                value={formatCurrency(calculatedValues.makingAmount)}
                readOnly
                className="bg-emerald-50 text-emerald-700 font-semibold text-base py-6"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="otherCharges" className="text-base">Other Charges (₹)</Label>
          <Input
            id="otherCharges"
            type="number"
            inputMode="numeric"
            value={estimateForm.otherCharges}
            onChange={(e) => setEstimateForm({ ...estimateForm, otherCharges: e.target.value })}
            placeholder="For miscellaneous expenses"
            className="text-base py-6"
          />
        </div>

        <div className="border-t pt-4">
          <Label className="text-base font-semibold">Estimate Status</Label>
          <div className="flex items-center space-x-8 mt-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                checked={estimateStatus === 'draft'}
                onChange={() => setEstimateStatus('draft')}
                className="w-5 h-5 text-emerald-600"
              />
              <span className="text-base">Draft</span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                checked={estimateStatus === 'confirmed'}
                onChange={() => setEstimateStatus('confirmed')}
                className="w-5 h-5 text-emerald-600"
              />
              <span className="text-base">Confirmed</span>
            </label>
          </div>
        </div>

        <div className="space-y-3 border-t pt-4">
          <Label className="text-base">Photos (Optional)</Label>
          <div className="flex flex-wrap gap-2">
            {estimateImages.map((img, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                <div className="absolute inset-0 flex items-center justify-center">
                  <ImageOff className="w-6 h-6 text-gray-300" />
                </div>
                <img src={img.url} alt={`Upload ${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                <button
                  type="button"
                  onClick={() => setEstimateImages(estimateImages.filter((_, i) => i !== idx))}
                  className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {/* Camera capture button */}
            <button
              type="button"
              onClick={() => estimateCameraInputRef.current?.click()}
              disabled={uploadingImages}
              className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
              data-testid="estimate-camera-btn"
            >
              {uploadingImages ? (
                <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <><Camera className="w-5 h-5" /><span className="text-[10px] mt-0.5">Camera</span></>
              )}
            </button>
            {/* Gallery picker button */}
            <button
              type="button"
              onClick={() => estimateImageInputRef.current?.click()}
              disabled={uploadingImages}
              className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
              data-testid="estimate-upload-photo-btn"
            >
              {uploadingImages ? (
                <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <><Images className="w-5 h-5" /><span className="text-[10px] mt-0.5">Gallery</span></>
              )}
            </button>
          </div>
          {/* Gallery input */}
          <input
            ref={estimateImageInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={async (e) => {
              const files = Array.from(e.target.files);
              if (files.length === 0) return;
              setUploadingImages(true);
              try {
                const results = await Promise.all(files.map(f => fileService.uploadFile(f)));
                const newImages = results.map((r, i) => ({ url: r.url, name: files[i].name }));
                setEstimateImages(prev => [...prev, ...newImages]);
              } catch (err) {
                toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
              } finally {
                setUploadingImages(false);
                e.target.value = '';
              }
            }}
          />
          {/* Camera input — opens camera directly on Android */}
          <input
            ref={estimateCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={async (e) => {
              const files = Array.from(e.target.files);
              if (files.length === 0) return;
              setUploadingImages(true);
              try {
                const results = await Promise.all(files.map(f => fileService.uploadFile(f)));
                const newImages = results.map((r, i) => ({ url: r.url, name: files[i].name }));
                setEstimateImages(prev => [...prev, ...newImages]);
              } catch (err) {
                toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
              } finally {
                setUploadingImages(false);
                e.target.value = '';
              }
            }}
          />
        </div>

        <div className="border-t pt-4 bg-gray-50 rounded-lg p-4 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Breakdown</p>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Gold</span>
            <span className="font-medium">{formatCurrency(calculatedValues.goldAmount)}</span>
          </div>
          {calculatedValues.stonesAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Stones</span>
              <span className="font-medium">{formatCurrency(calculatedValues.stonesAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Making</span>
            <span className="font-medium">{formatCurrency(calculatedValues.makingAmount)}</span>
          </div>
          {parseFloat(estimateForm.otherCharges) > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Other</span>
              <span className="font-medium">{formatCurrency(parseFloat(estimateForm.otherCharges))}</span>
            </div>
          )}
          <div className="flex justify-between text-sm border-t pt-2 mt-1">
            <span className="text-gray-600">GST (3%)</span>
            <span className="font-medium">{formatCurrency(calculatedValues.gstAmount)}</span>
          </div>
        </div>
      </div>
    );

    return (
      <>
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => { setIsCreateDialogOpen(open); if (!open) setWizardStep(1); }}>
        <DialogContent className="fixed bottom-0 left-0 right-0 top-auto translate-x-0 translate-y-0 max-w-full w-full h-[92vh] flex flex-col p-0 gap-0 overflow-hidden rounded-t-2xl [&>button]:hidden border-0 shadow-xl"
          style={{animation: 'none'}}>
          {/* Sticky Wizard Header */}
          <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b bg-white">
            {/* Top row: [Back/Save] — centered title — [Next/X] */}
            <div className="flex items-center mb-2.5 gap-2">
              {/* Left slot: Back arrow (steps 2-4) | empty (step 1) */}
              <div className="w-16 flex-shrink-0 flex items-center">
                {wizardStep > 1 && (
                  <button
                    type="button"
                    onClick={() => setWizardStep(s => s - 1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors"
                    data-testid="wizard-back-btn"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                  </button>
                )}
              </div>

              {/* Center: step name */}
              <p className="flex-1 text-base font-bold text-gray-900 text-center">
                {stepTitles[wizardStep - 1]}
              </p>

              {/* Right slot: Save+X (step 4) | Next+X (steps 1-3) */}
              <div className="w-16 flex-shrink-0 flex items-center justify-end gap-1.5">
                {wizardStep === 4 ? (
                  <button
                    type="button"
                    onClick={handleCreateEstimate}
                    disabled={loading}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold px-3 py-2 rounded-lg flex items-center gap-1 disabled:opacity-60 min-h-[40px]"
                    data-testid="wizard-header-save-btn"
                  >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Save
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleWizardNext}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 transition-colors shadow-sm"
                    data-testid="wizard-next-btn"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsCreateDialogOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                  data-testid="wizard-close-btn"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
            {/* Bold summary cards — 3×2 grid */}
            <div className="grid grid-cols-3 gap-1">
              <div className="bg-gray-50 rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-gray-400 font-medium leading-none mb-1">Net Weight</p>
                <p className="text-sm font-bold text-gray-900 leading-none">{calculatedValues.netWeight.toFixed(2)}<span className="text-xs font-medium text-gray-500 ml-0.5">g</span></p>
              </div>
              <div className="bg-amber-50 rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-gray-400 font-medium leading-none mb-1">Gold</p>
                <p className="text-sm font-bold text-amber-700 leading-none">{formatCurrency(calculatedValues.goldAmount)}</p>
              </div>
              <div className="bg-purple-50 rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-gray-400 font-medium leading-none mb-1">Stones</p>
                <p className="text-sm font-bold text-purple-600 leading-none">{formatCurrency(calculatedValues.stonesAmount)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-gray-400 font-medium leading-none mb-1">MC Amount</p>
                <p className="text-sm font-bold text-blue-600 leading-none">{formatCurrency(calculatedValues.makingAmount)}</p>
              </div>
              <div className="bg-orange-50 rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-gray-400 font-medium leading-none mb-1">GST (3%)</p>
                <p className="text-sm font-bold text-orange-500 leading-none">{formatCurrency(calculatedValues.gstAmount)}</p>
              </div>
              <div className="bg-emerald-500 rounded-lg px-2.5 py-2">
                <p className="text-[9px] text-emerald-100 font-medium leading-none mb-1">Total</p>
                <p className="text-sm font-bold text-white leading-none">{formatCurrency(calculatedValues.totalAmount)}</p>
              </div>
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto px-5 py-5">
            {wizardStep === 1 && renderStep1()}
            {wizardStep === 2 && renderStep2()}
            {wizardStep === 3 && renderStep3()}
            {wizardStep === 4 && renderStep4()}
          </div>

        </DialogContent>
      </Dialog>

      {/* Today's Gold Rate not-updated gate (wizard entry-point) */}
      <GoldRateGateDialog
        {...goldGate.dialogProps}
        userRole={currentUser?.role}
        onUpdateNow={() => navigate('/gold-prices')}
      />
      </>
    );
  };


  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold truncate" data-testid="estimates-page-title">Estimates</h1>
            <p className="text-xs text-emerald-100">Manage customer estimates</p>
          </div>
          {(view === 'customers' || view === 'all-estimates') && (
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <div className="flex gap-2">
                <Button
                  onClick={() => currentUser?.role === 'admin' ? openExchangeDialog() : null}
                  disabled={currentUser?.role !== 'admin'}
                  className={`text-sm h-9 px-3 ${currentUser?.role === 'admin' ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-amber-200 text-amber-400 cursor-not-allowed opacity-60'}`}
                  data-testid="old-gold-btn"
                  title={currentUser?.role !== 'admin' ? 'Create Old Gold estimates via Sessions & Leads' : ''}
                >
                  Old Gold
                </Button>
                <Button
                  onClick={() => currentUser?.role === 'admin' ? openCreateDialog() : null}
                  disabled={currentUser?.role !== 'admin'}
                  className={`text-sm h-9 px-3 ${currentUser?.role === 'admin' ? 'bg-white text-emerald-700 hover:bg-emerald-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'}`}
                  data-testid="new-estimate-btn"
                  title={currentUser?.role !== 'admin' ? 'Create estimates via Sessions & Leads' : ''}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Estimate
                </Button>
              </div>
              {currentUser?.role !== 'admin' && (
                <button
                  onClick={() => navigate('/crm')}
                  className="text-[10px] text-emerald-200 underline underline-offset-2 hover:text-white"
                  data-testid="go-to-sessions-link"
                >
                  Create via Sessions &amp; Leads
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Toggle - By Customer / All Estimates */}
      {view !== 'customer-detail' && (
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex gap-1" data-testid="view-toggle">
          <button
            onClick={() => { setView('customers'); loadCustomers(statusFilter); }}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              view === 'customers'
                ? 'bg-[#075E54] text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="view-by-customer"
          >
            By Customer
          </button>
          <button
            onClick={() => { setView('all-estimates'); loadAllEstimates(); }}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              view === 'all-estimates'
                ? 'bg-[#075E54] text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="view-all-estimates"
          >
            All Estimates
          </button>
        </div>
      )}

      {/* Content */}
      {view === 'customers' && renderCustomersView()}
      {view === 'all-estimates' && renderAllEstimatesView()}
      {view === 'customer-detail' && renderCustomerDetailView()}

      {/* Create Dialog */}
      {renderCreateDialog()}

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg">
              {isCombinedShare ? `Share ${selectedEstimates.length} Combined Estimates` : 'Share Estimate'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button
              onClick={shareNative}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-base py-6 justify-start"
              data-testid="share-whatsapp-btn"
            >
              <Share2 className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div>Share via WhatsApp</div>
                <div className="text-xs opacity-80">Opens share sheet to pick WhatsApp</div>
              </div>
            </Button>

            <Button
              onClick={downloadImage}
              variant="outline"
              className="w-full text-base py-6 justify-start border-2"
              data-testid="download-estimate-btn"
            >
              <Download className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div>Download Image</div>
                <div className="text-xs opacity-80">Save estimate card to device</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)} className="w-full">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden Estimate Card for Image Generation */}
      {estimateToShare && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <EstimateCard ref={estimateCardRef} estimate={estimateToShare} />
        </div>
      )}

      {/* Hidden Combined Estimate Card for Image Generation */}
      {isCombinedShare && selectedEstimates.length > 0 && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <CombinedEstimateCard
            ref={combinedCardRef}
            estimates={selectedEstimates}
            customerName={selectedCustomer?.customer_name}
            customerPhone={selectedCustomer?.customer_phone}
          />
        </div>
      )}

      {/* Exchange Estimate Dialog */}
      <Dialog open={isExchangeDialogOpen} onOpenChange={setIsExchangeDialogOpen}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-amber-700">Old Gold Exchange Estimate</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Customer */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-sm">Customer Phone *</Label>
                <PhoneInput
                  value={normalizePhone(exchangeForm.customerPhone)}
                  onChange={val => setExchangeForm(prev => ({ ...prev, customerPhone: val }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Customer Name *</Label>
                <Input value={exchangeForm.customerName} onChange={(e) => setExchangeForm({ ...exchangeForm, customerName: e.target.value })} className="py-5" />
              </div>
            </div>

            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Weight Details</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Gross Weight (g) *</Label>
                  <Input type="number" inputMode="decimal" step="0.001" value={exchangeForm.grossWeight} onChange={(e) => setExchangeForm({ ...exchangeForm, grossWeight: e.target.value })} className="py-5" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Less Dust (g)</Label>
                  <Input type="number" inputMode="decimal" step="0.001" value={exchangeForm.lessDust} onChange={(e) => setExchangeForm({ ...exchangeForm, lessDust: e.target.value })} className="py-5" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Less Stones (g)</Label>
                  <Input type="number" inputMode="decimal" step="0.001" value={exchangeForm.lessStones} onChange={(e) => setExchangeForm({ ...exchangeForm, lessStones: e.target.value })} className="py-5" />
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Net Weight</span>
                  <span className="font-bold text-emerald-700">{exchangeCalc.netWeight.toFixed(3)}g</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Purity & Refining</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Approx Purity (%) *</Label>
                  <Input type="number" inputMode="decimal" step="0.1" value={exchangeForm.approxPurity} onChange={(e) => setExchangeForm({ ...exchangeForm, approxPurity: e.target.value })} placeholder="e.g. 85" className="py-5" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Refining Loss (%)</Label>
                  <Input type="number" inputMode="decimal" step="0.1" value={exchangeForm.refiningPercentage} onChange={(e) => setExchangeForm({ ...exchangeForm, refiningPercentage: e.target.value })} placeholder="e.g. 5" className="py-5" />
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Fine Weight (Net × Purity)</span>
                  <span className="font-bold text-emerald-700">{exchangeCalc.fineWeight.toFixed(3)}g</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Final Gold Weight (after refining)</span>
                  <span className="font-bold text-emerald-700">{exchangeCalc.finalGoldWeight.toFixed(3)}g</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Gold Rate &amp; Value</h3>
              <div className="space-y-1">
                <Label className="text-sm">Gold Rate (₹/gram) *</Label>
                <Input type="number" inputMode="numeric" value={exchangeForm.goldRate} onChange={(e) => setExchangeForm({ ...exchangeForm, goldRate: e.target.value })} className="py-5" />
              </div>
            </div>

            {/* Stones in Gold */}
            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Stones in Gold <span className="text-xs font-normal text-gray-400">(optional)</span></h3>
              <div className="grid grid-cols-12 gap-1.5 items-end">
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs text-gray-500">Name</Label>
                  <Input placeholder="Diamond" value={currentExchangeStone.name}
                    onChange={e => setCurrentExchangeStone(s => ({ ...s, name: e.target.value }))}
                    className="h-9 text-sm" />
                </div>
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs text-gray-500">Wt (ct)</Label>
                  <Input type="number" inputMode="decimal" step="0.01" placeholder="0.00"
                    value={currentExchangeStone.weight}
                    onChange={e => {
                      const w = e.target.value;
                      const auto = w && currentExchangeStone.rate ? String(parseFloat(w) * parseFloat(currentExchangeStone.rate)) : '';
                      setCurrentExchangeStone(s => ({ ...s, weight: w, amount: auto }));
                    }}
                    className="h-9 text-sm" />
                </div>
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs text-gray-500">Rate ₹</Label>
                  <Input type="number" inputMode="numeric" placeholder="0"
                    value={currentExchangeStone.rate}
                    onChange={e => {
                      const r = e.target.value;
                      const auto = r && currentExchangeStone.weight ? String(parseFloat(currentExchangeStone.weight) * parseFloat(r)) : '';
                      setCurrentExchangeStone(s => ({ ...s, rate: r, amount: auto }));
                    }}
                    className="h-9 text-sm" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs text-gray-500">Amt ₹</Label>
                  <Input type="number" inputMode="numeric" placeholder="auto"
                    value={currentExchangeStone.amount}
                    onChange={e => setCurrentExchangeStone(s => ({ ...s, amount: e.target.value }))}
                    className="h-9 text-sm" />
                </div>
                <div className="col-span-1">
                  <button type="button" onClick={handleAddExchangeStone}
                    className="h-9 w-full flex items-center justify-center rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {exchangeStones.length > 0 && (
                <div className="space-y-1.5">
                  {exchangeStones.map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-gray-800">{s.name || 'Stone'}</span>
                        <span className="text-gray-500 ml-2">{s.weight}ct @ ₹{s.rate.toLocaleString('en-IN')}/ct</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-semibold text-amber-700">₹{Math.round(s.amount).toLocaleString('en-IN')}</span>
                        <button type="button" onClick={() => setExchangeStones(prev => prev.filter((_, idx) => idx !== i))}
                          className="text-gray-300 hover:text-red-500 transition-colors">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold text-amber-700 px-3">
                    <span>Stones Total</span>
                    <span>₹{exchangeCalc.stonesTotal.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Final Exchange Value */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              {exchangeCalc.stonesTotal > 0 && (
                <div className="space-y-1 mb-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Gold Value</span>
                    <span>₹{exchangeCalc.goldValue.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Stones Value</span>
                    <span>₹{exchangeCalc.stonesTotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="border-t border-amber-200 pt-1" />
                </div>
              )}
              <p className="text-sm text-gray-600 text-center">Final Exchange Value</p>
              <p className="text-2xl font-bold text-amber-700 text-center">{formatCurrency(exchangeCalc.exchangeValue)}</p>
              <p className="text-xs text-gray-500 text-center">
                {exchangeCalc.finalGoldWeight.toFixed(3)}g × ₹{(parseFloat(exchangeForm.goldRate) || 0).toLocaleString('en-IN')}/g
                {exchangeCalc.stonesTotal > 0 && ` + ₹${exchangeCalc.stonesTotal.toLocaleString('en-IN')} stones`}
              </p>
            </div>

            {/* Cash section */}
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="cashRequired" checked={exchangeForm.cashRequired} onChange={(e) => setExchangeForm({ ...exchangeForm, cashRequired: e.target.checked })} className="w-4 h-4 text-emerald-600" />
                <Label htmlFor="cashRequired" className="cursor-pointer text-sm font-semibold">Cash Required?</Label>
              </div>
              {exchangeForm.cashRequired && (
                <>
                  <div className="space-y-1">
                    <Label className="text-sm">Cash Deduction (%)</Label>
                    <Input type="number" inputMode="decimal" step="0.1" value={exchangeForm.cashDeductionPercentage} onChange={(e) => setExchangeForm({ ...exchangeForm, cashDeductionPercentage: e.target.value })} placeholder="e.g. 3" className="py-5" />
                  </div>
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600">Final Cash Amount</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(exchangeCalc.finalCash)}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsExchangeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateExchangeEstimate} className="bg-amber-500 hover:bg-amber-600">Save Exchange Estimate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Estimates;
